"use client"

/**
 * A question's Category: one choice, one field.
 *
 * Faculty and the program director use the same one, the course content
 * category. Program-level standards (NCLEX client needs, program competencies)
 * are not asked of an author here; the director defines and reports on them in
 * Settings. Using a category in a question is separate from defining one.
 *
 * Leo's help is the Leo card the rest of the editor uses, headed "Leo
 * suggests". Nothing is applied until the author clicks, and the tag records
 * who placed it.
 */

import * as React from "react"

import { ChosenCategory } from "@/components/question-composer/chosen-categories"
import { Button } from "@/components/ui/button"
import { Tip } from "@/components/ui/tip"
import { useQuestionDimensions } from "@/hooks/use-category-dimensions"
import { useExamCategories } from "@/hooks/use-exam-categories"
import {
  buildDimensionLookup,
  type CategoryProvenanceMap,
  type ExamCategory,
} from "@/lib/exam-categories"

export function CategoryField({
  value,
  provenance,
  onChange,
  onOpen,
  courseName,
  blockedReason,
  disabled = false,
  label = "Category",
}: {
  /** The heading: the content type's name in Settings › Classification. */
  label?: string
  /** Every tag on the question. Only the course-content ones are read here. */
  value: string[]
  provenance: CategoryProvenanceMap
  /** `aiIds` marks the tag as a Leo suggestion the author approved. */
  onChange: (ids: string[], meta?: { aiIds?: string[] }) => void
  onOpen: () => void
  /** The categories the chosen course teaches. Unused here now that Leo's suggestions sit at the top of Properties; kept for callers. */
  scopeIds?: readonly string[]
  /** The course the path starts from. */
  courseName?: string
  /** Why the field cannot be used yet (no course chosen). */
  blockedReason?: string
  disabled?: boolean
}) {
  const { categories } = useExamCategories()
  const dimension = useQuestionDimensions()[0]
  const lookup = React.useMemo(() => buildDimensionLookup(categories), [categories])

  const chosen = React.useMemo(
    () =>
      value
        .filter((id) => dimension && lookup.get(id) === dimension.id)
        .map((id) => categories.find((category) => category.id === id))
        .filter((category): category is ExamCategory => Boolean(category)),
    [categories, dimension, lookup, value],
  )
  const rootLabel = courseName ?? dimension?.name ?? ""
  const remove = (id: string) => onChange(value.filter((existing) => existing !== id))

  // No category yet. Leo's suggestions are in the classification card at the top, not here.
  const emptyState = (
    <div className="group flex list-none items-start gap-1 rounded-md border border-border bg-background hover:bg-muted/40">
      <button
        type="button"
        disabled={disabled || Boolean(blockedReason)}
        onClick={onOpen}
        aria-label="Choose a category"
        className="flex min-w-0 flex-1 items-start gap-3 rounded-md px-3 py-2.5 text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span
          className="inline-flex size-9 shrink-0 items-center justify-center rounded-md bg-[var(--icon-disc-brand-bg)] text-[var(--icon-disc-brand-fg)]"
          aria-hidden="true"
        >
          <i className="fa-light fa-tag text-base" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="break-words text-sm font-medium leading-snug text-muted-foreground">
            No category yet
          </span>
          <span className="line-clamp-3 break-words text-xs leading-snug text-muted-foreground">
            {blockedReason ?? "Choose one from this course's list."}
          </span>
        </span>
      </button>
    </div>
  )

  return (
    <section
      aria-label={label}
      data-category-dimension={dimension?.id}
      className="flex flex-col gap-3"
    >
      <div className="flex items-center gap-2">
        <h2 className="m-0 text-sm font-semibold text-foreground">{label}</h2>
        {(
          <Tip label="Add category" side="top">
            <Button
              type="button"
              variant="outline"
              size="icon-sm"
              className="ms-auto"
              aria-label="Add category"
              disabled={disabled || Boolean(blockedReason)}
              onClick={onOpen}
            >
              <i className="fa-light fa-plus text-xs" aria-hidden="true" />
            </Button>
          </Tip>
        )}
      </div>

      {chosen.length > 0 ? (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {chosen.map((category) => (
            <ChosenCategory
              key={category.id}
              category={category}
              categories={categories}
              rootLabel={rootLabel}
              record={provenance[category.id]}
              disabled={disabled}
              onOpen={blockedReason ? () => {} : onOpen}
              onRemove={() => remove(category.id)}
            />
          ))}
        </ul>
      ) : (
        emptyState
      )}

    </section>
  )
}
